import { Link } from "@tanstack/react-router";
import CheckCircle from "~icons/material-symbols/check-circle";
import Info from "~icons/material-symbols/info-outline";
import { css } from "../../../../styled-system/css";
import { t } from "../../../libs/i18n";
import type { R7MarumoriCampaignBannerType } from "../utils/r7-marumori-campaign-banner";

type R7MarumoriCampaignBannerProps = {
  bannerType: R7MarumoriCampaignBannerType;
  onClickApply: () => void;
};

export const R7MarumoriCampaignBanner = ({
  bannerType,
  onClickApply,
}: R7MarumoriCampaignBannerProps) => {
  if (bannerType === "hidden") {
    return null;
  }

  return (
    <section
      className={css({
        bg: "surface.accentPrimaryLight",
        rounded: "xl",
        p: "16px",
        display: "flex",
        flexDir: "column",
        gap: "16px",
      })}
    >
      {bannerType === "not-applied" && (
        <NotAppliedContent onClickApply={onClickApply} />
      )}
      {bannerType === "applied" && <AppliedContent />}
      {bannerType === "ineligible" && <IneligibleContent />}
      {bannerType === "campaign-ended" && <CampaignEndedContent />}

      <div
        className={css({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        })}
      >
        <Link
          to="/about-campaign"
          className={css({
            flexGrow: 1,
            color: "text.accentPrimary",
            textDecoration: "underline",
          })}
        >
          {t("キャンペーン詳細")}
        </Link>
      </div>
    </section>
  );
};

const NotAppliedContent = ({ onClickApply }: { onClickApply: () => void }) => (
  <>
    <div
      className={css({
        display: "flex",
        flexDir: "column",
        gap: "10px",
      })}
    >
      <div
        className={css({
          display: "flex",
          alignItems: "center",
          gap: "6px",
        })}
      >
        <Info
          className={css({
            color: "text.accentPrimary",
            flexShrink: 0,
          })}
        />
        <p>{t("ポイント抽選に参加しましょう！")}</p>
      </div>

      <p
        className={css({
          color: "text.secondary",
          fontSize: "14px",
        })}
      >
        {t(
          "即時抽選キャンペーンに参加して、その場で2,000円分のポイントが当たるチャンス！",
        )}
      </p>
    </div>

    <button
      type="button"
      className={css({
        bg: "surface.accentPrimary",
        rounded: "2xl",
        px: "14px",
        py: "12px",
        color: "white",
        fontWeight: 500,
      })}
      onClick={onClickApply}
    >
      {t("応募する")}
    </button>
  </>
);

const AppliedContent = () => (
  <>
    <div
      className={css({
        display: "flex",
        flexDir: "column",
        gap: "10px",
      })}
    >
      <div
        className={css({
          display: "flex",
          alignItems: "center",
          gap: "6px",
        })}
      >
        <Info
          className={css({
            color: "text.accentPrimary",
            flexShrink: 0,
          })}
        />
        <p>{t("ポイント抽選に参加しましょう！")}</p>
      </div>

      <p
        className={css({
          color: "text.secondary",
          fontSize: "14px",
        })}
      >
        {t(
          "即時抽選キャンペーンに参加して、その場で2,000円分のポイントが当たるチャンス！",
        )}
      </p>
    </div>

    <button
      type="button"
      className={css({
        bg: "surface.disable",
        rounded: "2xl",
        px: "14px",
        py: "12px",
        color: "text.disable",
        fontWeight: 500,
      })}
      disabled
    >
      {t("応募する")}
    </button>

    <div
      className={css({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
      })}
    >
      <CheckCircle
        className={css({
          color: "text.accentSuccess",
          width: "20px",
          height: "20px",
        })}
      />
      <p
        className={css({
          color: "text.accentSuccess",
          fontSize: "14px",
        })}
      >
        {t("応募済み")}
      </p>
    </div>
  </>
);

const IneligibleContent = () => (
  <div
    className={css({
      display: "flex",
      flexDir: "column",
      gap: "10px",
    })}
  >
    <div
      className={css({
        display: "flex",
        alignItems: "center",
        gap: "6px",
      })}
    >
      <Info
        className={css({
          color: "text.accentPrimary",
          flexShrink: 0,
        })}
      />
      <p>{t("令和7年度まるもり地域ポイント2,000円分プレゼントキャンペーン")}</p>
    </div>

    <p
      className={css({
        color: "text.secondary",
        fontSize: "14px",
      })}
    >
      {t(
        "本キャンペーンへの参加はおひとりさま1回までとなります。令和6年度にポイントを受け取られた方はお申し込みできません。",
      )}
    </p>
  </div>
);

const CampaignEndedContent = () => (
  <div
    className={css({
      display: "flex",
      flexDir: "column",
      gap: "10px",
    })}
  >
    <div
      className={css({
        display: "flex",
        alignItems: "center",
        gap: "6px",
      })}
    >
      <Info
        className={css({
          color: "text.accentPrimary",
          flexShrink: 0,
        })}
      />
      <p>{t("ポイント抽選キャンペーンは終了しました")}</p>
    </div>

    <p
      className={css({
        color: "text.secondary",
        fontSize: "14px",
      })}
    >
      {t(
        "当選者が上限に達したため、令和7年度の2,000円分ポイント抽選キャンペーンは終了しました。",
      )}
    </p>
  </div>
);
